import React from "react";
import {
  ChevronRight,
  HardDriveUpload,
  Layers3,
  LogOut,
  Settings,
  ShieldCheck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, type To, useNavigate } from "react-router-dom";
import { useLogin } from "../../reducers";
import { LOGOUT_ROUTE } from "../../api";
import { useActionItems } from "./ActionItemsContext";
import { unanalyzedSessionsHref } from "./unanalyzedSessionsHref";
import { pendingReviewHref } from "./pendingReviewHref";

/** Set by DashboardTopBar on the wrapper holding the avatar and this menu. */
const PROFILE_MENU_SELECTOR = "[data-profile-menu]";

/** One thing the user has to do, leading to where it is done. */
const ToDoItem = ({
  to,
  icon,
  title,
  hint,
  onClick,
}: {
  to: To;
  icon: React.ReactNode;
  title: string;
  hint: string;
  onClick: () => void;
}) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm transition hover:bg-muted-surface"
  >
    {icon}
    <span className="min-w-0 flex-1">
      <span className="block font-medium text-text">{title}</span>
      <span className="block text-xs text-text-dim">{hint}</span>
    </span>
    <ChevronRight size={16} className="mt-0.5 shrink-0 text-text-dim" />
  </Link>
);

type DashboardProfileDropdownProps = {
  onClose: () => void;
  onShowCapturePrivacy: () => void;
  showCapturePrivacy?: boolean;
};

const DashboardProfileDropdown = ({
  onClose,
  onShowCapturePrivacy,
  showCapturePrivacy = true,
}: DashboardProfileDropdownProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useLogin();
  const { pendingReview, unanalyzed } = useActionItems();
  const fullName = `${user.first_name} ${user.last_name}`.trim();
  const displayName =
    fullName && fullName.toLowerCase() !== "no user" ? fullName : user.username || "User";
  const displayEmail =
    user.email || (user.username.includes("@") ? user.username : "No email available");
  const displayInitial = (user.first_name || user.username || user.email || "U")
    .charAt(0)
    .toUpperCase();

  // A document listener rather than a full-screen backdrop: the toolbar this
  // menu lives in is backdrop-filtered, which makes it the containing block
  // for `fixed` descendants, so a backdrop would only cover the toolbar. The
  // avatar is inside the anchor, so clicking it closes here instead of
  // re-opening via its own toggle.
  React.useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest?.(PROFILE_MENU_SELECTOR)) {
        onClose();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [onClose]);

  return (
    <>
      {/* Hangs off the avatar, so it follows whatever inset the toolbar has.
          app-no-drag: the toolbar around it is the window's drag region. */}
      <div className="app-no-drag absolute right-0 top-full z-40 mt-1 w-[260px] overflow-hidden rounded-xl border border-outline bg-surface shadow-lg">
        <div className="border-b border-outline px-4 py-3.5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-base font-bold text-on-accent">
              {displayInitial}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-text">{displayName}</div>
              <div className="truncate text-xs text-text-dim">{displayEmail}</div>
            </div>
          </div>
        </div>

        {(pendingReview > 0 || unanalyzed > 0) && (
          <div className="border-b border-outline p-1.5">
            <p className="px-3 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wider text-text-dim">
              {t("activity.to_do")}
            </p>
            {/* First, as a session is reviewed before it uploads and can be analyzed. */}
            {pendingReview > 0 && (
              <ToDoItem
                to={pendingReviewHref(user.id)}
                icon={<HardDriveUpload size={16} className="mt-0.5 shrink-0 text-unread" />}
                title={t("activity.review_sessions", { count: pendingReview })}
                hint={t("dashboard.pending_review_description")}
                onClick={onClose}
              />
            )}
            {/* Lands on the user's own sessions over all time, so every one counted here is listed. */}
            {unanalyzed > 0 && (
              <ToDoItem
                to={unanalyzedSessionsHref(user.id)}
                icon={<Layers3 size={16} className="mt-0.5 shrink-0 text-unread" />}
                title={t("activity.unanalyzed_sessions", { count: unanalyzed })}
                hint={t("activity.unanalyzed_sessions_hint")}
                onClick={onClose}
              />
            )}
          </div>
        )}

        <div className="p-1.5">
          <button
            type="button"
            onClick={() => {
              navigate("/settings/");
              onClose();
            }}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-text-mid transition hover:bg-muted-surface"
          >
            <Settings size={16} className="text-text-dim" />
            {t("Settings")}
          </button>
          {showCapturePrivacy && (
            <button
              type="button"
              onClick={onShowCapturePrivacy}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-text-mid transition hover:bg-muted-surface"
            >
              <ShieldCheck size={16} className="text-text-dim" />
              {t("what_platonic_records")}
            </button>
          )}
        </div>

        <div className="border-t border-outline p-1.5">
          <Link to={LOGOUT_ROUTE} reloadDocument>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-danger transition hover:bg-danger/10"
            >
              <LogOut size={16} />
              Sign out
            </button>
          </Link>
        </div>
      </div>
    </>
  );
};

export default DashboardProfileDropdown;
